import React, { useCallback, useMemo } from 'react';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { IntValueOrVariableSelect } from '../../ConditionListForm/IntValueOrVariableSelect';

interface HubValueSelectProps {
  subConditionUuid: string;
  intValue?: string;
  varValue?: number;
  variables: MemoryDTO[];
  excludeVariableId?: number;
  onChange: (data: { intValue?: string; varValue?: number }) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * UUID-based ID wrapper around IntValueOrVariableSelect.
 * Generates stable DOM IDs from subConditionUuid instead of positional stepIndex/switchIndex,
 * preventing stale/colliding HTML IDs after condition reorder.
 *
 * Also enforces integer-only input via onKeyDown/onPaste filters,
 * matching the behavior of ConditionListItemForm.
 */
export function HubValueSelect({ subConditionUuid, onChange, ...rest }: HubValueSelectProps) {
  // Deterministic numeric DOM IDs derived from UUID hex — no global state, no memory leak
  const { stepId, switchId } = useMemo(() => {
    const hex = subConditionUuid.replace(/-/g, '');
    return {
      stepId: parseInt(hex.slice(0, 8), 16) % 100000000,
      switchId: parseInt(hex.slice(8, 16), 16) % 100000000,
    };
  }, [subConditionUuid]);

  /** Allow only digits, minus, navigation keys, and keyboard shortcuts (Ctrl/Cmd+C/V/X/A) */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    ];

    if (event.metaKey || event.ctrlKey) return;
    if (allowedKeys.includes(event.key)) return;
    if (event.key >= '0' && event.key <= '9') return;

    // Allow minus only at the start of input (for negative numbers)
    if (event.key === '-') {
      const input = event.target as HTMLInputElement;
      if (input.selectionStart === 0 && !input.value.includes('-')) {
        return; // let the browser handle it naturally
      }
      event.preventDefault();
      return;
    }

    event.preventDefault();
  }, []);

  /** Strip non-integer characters from pasted text */
  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text').trim();
    const isValidInteger = /^-?\d+$/.test(pastedText);

    let finalValue: string;
    if (isValidInteger) {
      finalValue = pastedText;
    } else {
      const parsedValue = parseInt(pastedText, 10);
      finalValue = isNaN(parsedValue) ? '0' : String(parsedValue);
    }

    onChange({ intValue: finalValue });
  }, [onChange]);

  return (
    <IntValueOrVariableSelect
      {...rest}
      onChange={onChange}
      stepIndex={stepId}
      switchIndex={switchId}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
}
